import React, { useMemo, useState } from "react";
import { Alert, Button, Chip, Skeleton } from "@mui/material";
import {
  ArrowBack,
  DeleteOutline,
  EditOutlined,
} from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AppLayout from "../../lib/components/Layout/app-layout";
import Header from "../../lib/components/Header";
import AppSelectField from "../../lib/components/AppSelectField";
import ConfirmationDialog from "../../lib/components/ConfirmationDialog";
import BTNLoader from "../../lib/components/BTNLoader";
import marketplaceService from "../../service/marketplaceService";
import organizationservice from "../../service/organizationservice";
import pathnames, { marketplaceItemEditPath } from "../../constants/pathnames";
import errorHandler from "../../utils/errorHandler";
import { moneyFormat } from "../../utils/moneyFormat";
import MarketplaceStatusBadge from "./components/MarketplaceStatusBadge";
import {
  ITEM_STATUS_FORM_OPTIONS,
  formatCategoryLabel,
  formatDateTime,
  getMonthlyAmount,
  getPricingRows,
  isItemLocked,
  normalizePlans,
  plansGrantingRule,
  resolveEligiblePlans,
} from "./utils";
import "./marketplace.scss";

/**
 * Full-page catalogue item inspector (B1 single + B4 lifecycle).
 */
const MarketplaceItemDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [statusValue, setStatusValue] = useState("");

  const itemQuery = useQuery({
    retry: false,
    enabled: Boolean(id),
    queryKey: ["marketplace-item", id],
    queryFn: () => marketplaceService.getItem(id),
  });

  const plansQuery = useQuery({
    retry: false,
    queryKey: ["subscription-plans"],
    queryFn: () => organizationservice.getSubscriptionPlans(),
  });

  const rulesQuery = useQuery({
    retry: false,
    queryKey: ["marketplace-rules"],
    queryFn: () => marketplaceService.getRules(),
  });

  const item = itemQuery.data;
  const plans = useMemo(() => normalizePlans(plansQuery.data), [plansQuery.data]);
  const rules = useMemo(() => rulesQuery.data || [], [rulesQuery.data]);
  const rulesByCode = useMemo(() => {
    const map = {};
    rules.forEach((r) => {
      map[r.rule] = r;
    });
    return map;
  }, [rules]);

  const locked = isItemLocked(item);
  const eligible = resolveEligiblePlans(item, plans);
  const excludedIds = new Set(
    (item?.eligibility?.excludePlanIds || []).map(String)
  );
  const excludedPlans = plans.filter((p) => excludedIds.has(String(p.id)));
  const ruleList = Array.isArray(item?.rules) ? item.rules : item?.rule ? [item.rule] : [];
  const monthlyAmount = getMonthlyAmount(item);
  const pricingRows = useMemo(() => getPricingRows(item), [item]);

  React.useEffect(() => {
    if (item?.status) setStatusValue(item.status);
  }, [item?.status]);

  const statusMutation = useMutation({
    mutationFn: (status) => marketplaceService.updateItemStatus(id, status),
    onError: ({ response }) => errorHandler(response),
    onSuccess: () => {
      toast.success("Item status updated");
      queryClient.invalidateQueries({ queryKey: ["marketplace-item", id] });
      queryClient.invalidateQueries({ queryKey: ["marketplace-items"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => marketplaceService.deleteItem(id),
    onError: ({ response }) => errorHandler(response),
    onSuccess: () => {
      toast.success("Item permanently deleted");
      navigate(pathnames.MARKETPLACE_ADDONS);
    },
  });

  if (itemQuery.isPending) {
    return (
      <AppLayout>
        <Header title="Add-on Details" description="Loading catalogue item…" />
        <div className="p-3">
          <Skeleton height={40} className="mb-3" />
          <Skeleton height={200} />
        </div>
      </AppLayout>
    );
  }

  if (!item) {
    return (
      <AppLayout>
        <Header title="Add-on Details" description="Item not found" />
        <div className="p-3">
          <Alert severity="warning" className="mb-3">
            Could not load this catalogue item.
          </Alert>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(pathnames.MARKETPLACE_ADDONS)}
          >
            Back to catalogue
          </Button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Header
        title={item.name || item.code}
        description={
          <span className="d-flex align-items-center gap-2 flex-wrap text-secondary fs-sm">
            <span className="mono">{item.code}</span>
            <span>·</span>
            <span>{formatCategoryLabel(item, rulesByCode)}</span>
            <span>·</span>
            <span>{item.kind}</span>
          </span>
        }
      />
      <div className="p-3 bg-light marketplace-page" style={{ minHeight: "100%" }}>
        <div className="mp-detail-toolbar">
          <Button
            startIcon={<ArrowBack />}
            color="inherit"
            onClick={() => navigate(pathnames.MARKETPLACE_ADDONS)}
          >
            Back
          </Button>
          <div className="d-flex flex-wrap gap-2">
            <Button
              variant="outlined"
              startIcon={<EditOutlined />}
              onClick={() => navigate(marketplaceItemEditPath(id))}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutline />}
              disabled={locked}
              onClick={() => setDeleteOpen(true)}
            >
              Hard Delete
            </Button>
          </div>
        </div>

        {locked ? (
          <Alert severity="info" className="mb-3">
            purchaseCount = {item.purchaseCount ?? 0}. Kind, capability, and unit value
            are locked. Hard delete is blocked — use status RETIRED to stop renewals.
          </Alert>
        ) : (
          <Alert severity="success" className="mb-3">
            Never purchased — kind/capability can still be edited and hard delete is allowed.
          </Alert>
        )}

        <div className="row g-3">
          <div className="col-12 col-lg-8">
            <div className="marketplace-drawer-section">
              <h6>Overview</h6>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <MarketplaceStatusBadge status={item.status} />
                <span className="marketplace-badge is-plan">{item.kind}</span>
                {item.isPurchasable ? (
                  <Chip size="small" label="Purchasable" color="success" variant="outlined" />
                ) : (
                  <Chip size="small" label="Not purchasable" variant="outlined" />
                )}
                {item.isRenewable ? (
                  <Chip size="small" label="Renewable" color="primary" variant="outlined" />
                ) : (
                  <Chip size="small" label="Not renewable" variant="outlined" />
                )}
              </div>
              <div className="fs-5 fw-semibold mb-1">{item.name}</div>
              <div className="text-secondary fs-sm mono mb-3">{item.code}</div>
              <p className="mb-0">{item.description || "No description provided."}</p>
            </div>

            <div className="marketplace-drawer-section">
              <h6>Pricing</h6>
              <div className="fs-3 fw-semibold mb-1">{moneyFormat(monthlyAmount)}</div>
              <div className="text-secondary fs-sm mb-3">Monthly list price</div>
              <div className="table-responsive">
                <table className="mp-price-table mb-0">
                  <thead>
                    <tr>
                      <th>Interval</th>
                      <th>Amount</th>
                      <th>Active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pricingRows.map((row) => (
                      <tr key={row.key}>
                        <td>{row.label}</td>
                        <td className="mono">{moneyFormat(row.amount)}</td>
                        <td>
                          <span
                            className={`marketplace-badge ${
                              row.isActive ? "is-active" : "is-disabled"
                            }`}
                          >
                            {row.isActive ? "Yes" : "No"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {item.kind === "LIMIT" || item.quantity ? (
              <div className="marketplace-drawer-section">
                <h6>Capacity</h6>
                <div className="row g-3">
                  <div className="col-6 col-md-3">
                    <div className="text-secondary fs-xs">Unit value</div>
                    <div className="fw-semibold">{item.unitValue ?? "—"}</div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-secondary fs-xs">Qty min</div>
                    <div className="fw-semibold">{item.quantity?.min ?? "—"}</div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-secondary fs-xs">Qty max</div>
                    <div className="fw-semibold">{item.quantity?.max ?? "—"}</div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-secondary fs-xs">Qty step</div>
                    <div className="fw-semibold">{item.quantity?.step ?? "—"}</div>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="marketplace-drawer-section">
              <h6>Capabilities</h6>
              {ruleList.length ? (
                <div className="d-flex flex-column gap-3">
                  {ruleList.map((rule, idx) => {
                    const meta = rulesByCode[rule];
                    const granted = plansGrantingRule(meta);
                    return (
                      <div key={rule} className="mp-rule-detail">
                        <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                          <span className="fw-semibold">
                            {meta?.label || rule}
                          </span>
                          <span className="marketplace-badge is-category">
                            {idx === 0 ? "Primary" : "Bundle"}
                          </span>
                          {meta?.type ? (
                            <span className="marketplace-badge is-plan">{meta.type}</span>
                          ) : null}
                        </div>
                        <div className="text-secondary fs-xs mono mb-1">{rule}</div>
                        {meta?.hint ? (
                          <div className="fs-sm text-secondary mb-1">{meta.hint}</div>
                        ) : null}
                        {granted.length ? (
                          <div className="d-flex flex-wrap gap-1 mt-1">
                            {granted.map((p) => (
                              <span key={p.id} className="marketplace-badge is-plan">
                                {p.name}
                                {p.unlimited
                                  ? ": ∞"
                                  : p.value != null
                                    ? `: ${p.value}`
                                    : ""}
                              </span>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <span className="text-secondary fs-sm">No rules linked</span>
              )}
            </div>
          </div>

          <div className="col-12 col-lg-4">
            <div className="marketplace-drawer-section">
              <h6>Lifecycle Status</h6>
              <p className="fs-sm text-secondary">
                ACTIVE sells &amp; renews · HIDDEN stops new sales · RETIRED stops renewals
              </p>
              <AppSelectField
                value={statusValue}
                onChange={(e) => setStatusValue(e.target.value)}
                items={ITEM_STATUS_FORM_OPTIONS}
              />
              <Button
                className="mt-2"
                fullWidth
                variant="contained"
                disabled={
                  statusMutation.isPending || !statusValue || statusValue === item.status
                }
                startIcon={<BTNLoader isLoading={statusMutation.isPending} />}
                onClick={() => statusMutation.mutate(statusValue)}
              >
                Update Status
              </Button>
            </div>

            <div className="marketplace-drawer-section">
              <h6>Can buy (eligible)</h6>
              <div className="d-flex flex-wrap gap-1">
                {eligible.length ? (
                  eligible.map((p) => (
                    <span key={p.id} className="marketplace-badge is-plan">
                      {p.name}
                    </span>
                  ))
                ) : (
                  <span className="text-secondary fs-sm">
                    No plans — all base plans are excluded
                  </span>
                )}
              </div>
              {excludedPlans.length ? (
                <>
                  <div className="text-secondary fs-xs mt-3 mb-1">Excluded</div>
                  <div className="d-flex flex-wrap gap-1">
                    {excludedPlans.map((p) => (
                      <span key={p.id} className="marketplace-badge is-disabled">
                        {p.name}
                      </span>
                    ))}
                  </div>
                </>
              ) : null}
            </div>

            <div className="marketplace-drawer-section">
              <h6>Purchases</h6>
              <div className="fs-4 fw-semibold">{Number(item.purchaseCount ?? 0)}</div>
              <div className="text-secondary fs-sm">Lifetime purchases / grants</div>
            </div>

            <div className="marketplace-drawer-section">
              <h6>Audit</h6>
              <div className="fs-sm">
                <div className="mb-2">
                  <div className="text-secondary fs-xs">Created by</div>
                  <div>{item.createdBy?.name || "—"}</div>
                </div>
                <div className="mb-2">
                  <div className="text-secondary fs-xs">Created</div>
                  <div>{formatDateTime(item.createdAt)}</div>
                </div>
                <div>
                  <div className="text-secondary fs-xs">Updated</div>
                  <div>{formatDateTime(item.updatedAt)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmationDialog
        isOpen={deleteOpen}
        type="danger"
        onClose={() => setDeleteOpen(false)}
        title="Hard delete item"
        body={`Permanently delete <strong>${item.name}</strong>? Only allowed when purchaseCount is 0.`}
        actionTitle="Delete Forever"
        isLoading={deleteMutation.isPending}
        onSubmit={() => deleteMutation.mutate()}
      />
    </AppLayout>
  );
};

export default MarketplaceItemDetails;
